import { zodResolver } from '@hookform/resolvers/zod'
import Dialog, { DialogProps } from '@mui/material/Dialog'
import dayjs from 'dayjs'
import React, { ReactNode, useEffect, useState } from 'react'
import {
    FormProvider,
    SubmitHandler,
    UseFormReturn,
    useForm,
} from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ZodEffects, ZodIntersection, ZodObject, z } from 'zod'
import { InternalProps } from './utils'

export interface RootContextProps {
    open?: boolean
    onClose?: () => void
    onChange?: (data: any) => void
    loading?: boolean
    onSubmit?: SubmitHandler<any>
    onDelete?: SubmitHandler<any>
    setInternalProps: (props: Partial<InternalProps>) => void
    delete?: boolean
    toggleDelete?: () => void
    height?: string | number
    width?: string | number
    methods: UseFormReturn<any> & {
        control: UseFormReturn<any>['control'] & {
            _options: UseFormReturn<any>['control']['_options'] & {
                disabled?: boolean
            }
        }
    }
}

interface RootProps
    extends Omit<DialogProps, 'open' | 'onClose' | 'onSubmit' | 'onChange'>,
        Omit<
            RootContextProps,
            | 'methods'
            | 'delete'
            | 'toggleDelete'
            | 'deleteState'
            | 'setInternalProps'
        > {
    children?: ReactNode
    schema?: ZodObject<any> | ZodEffects<any> | ZodIntersection<any, any>
    validateOnDelete?: boolean
    disableFields?: boolean
    defaultValues?: any
    reValidateMode?: 'onSubmit' | 'onBlur' | 'onChange'
}

export type { RootProps as DialogRootProps }

export const RootContext = React.createContext<RootContextProps>(undefined!)

export function Root({
    children,
    open,
    onClose,
    sx,
    loading,
    onSubmit,
    schema: zodSchema,
    defaultValues,
    onChange,
    disableFields,
    onDelete,
    height,
    width = '720px',
    validateOnDelete,
    reValidateMode = 'onSubmit',
    ...props
}: RootProps) {
    height = height || `calc(100vh - 18rem)`
    const navigate = useNavigate()
    const location = useLocation()

    open = open === undefined ? true : open
    onClose = onClose ?? (() => navigate(-1))
    const [deleteDialog, setDeleteDialog] = useState(false)
    const [internalProps, setInternalProps] = useState<InternalProps>({})

    const methods = useForm({
        defaultValues: transformData(defaultValues),
        resolver: async (data, context, options) => {
            if (validateOnDelete && deleteDialog) {
                return zodResolver(
                    // @ts-ignore
                    z.object({
                        motivo: z.string().trim().min(1, 'Campo obrigatório'),
                    }),
                )(transformData(data), context, options).then(result => {
                    const errors = Object.keys(result.errors)

                    if (errors.length > 0) {
                        toast.error(
                            'Verifique os campos com erros: ' +
                                errors.join(','),
                        )
                    }

                    return result
                })
            } else if (deleteDialog) {
                return { values: data, errors: {} }
            } else if (zodSchema) {
                return zodResolver(
                    // @ts-ignore
                    zodSchema,
                )(transformData(data), context, options)
                    .then(result => {
                        console.log('data', data)
                        console.log('zod', result)

                        const errors = Object.keys(result.errors)

                        if (errors.length > 0) {
                            toast.error(
                                'Verifique os campos com erros: ' +
                                    errors.join(','),
                            )
                        }

                        return result
                    })
                    .catch(err => {
                        console.error('err', err)
                        toast.error('Há campos obrigatórios não preenchidos!')
                        return err
                    })
            }

            return { values: data, errors: {} }
        },
        reValidateMode,
    })

    const watch = methods.watch()
    const isSubmitSuccessful = methods.formState.isSubmitSuccessful

    methods.control._options = {
        ...methods.control._options,
        disabled: disableFields,
    } as any

    useEffect(() => onChange && onChange(watch), [watch, onChange])

    useEffect(() => {
        if (!open) {
            methods.reset()
        }
    }, [open])

    useEffect(() => {
        if (location.hash && open) {
            const fieldElement = document?.getElementsByName(
                location.hash.replace('#', ''),
            )?.[0]
            if (fieldElement) {
                setTimeout(() => {
                    fieldElement.scrollIntoView({ behavior: 'smooth' })
                    fieldElement.focus()
                }, 600)
            }
        }
    }, [location.hash, open])

    useEffect(() => {
        if (isSubmitSuccessful && internalProps.closeOnSubmit) {
            onClose?.()
        }
    }, [internalProps.closeOnSubmit, isSubmitSuccessful])

    const context = {
        open,
        onClose,
        height,
        width,
        loading,
        onSubmit,
        methods,
        delete: deleteDialog,
        onDelete,
        toggleDelete: () => setDeleteDialog(state => !state),
        setInternalProps: props => {
            setInternalProps(state => ({ ...state, ...props }))
        },
    } satisfies RootContextProps

    return (
        <Dialog
            open={open}
            onClose={onClose}
            scroll="paper"
            // onKeyDown={onKeyDown}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
            PaperProps={{
                sx: {
                    backgroundColor: 'background.default',
                    maxHeight: '100vh',
                    overflowY: 'hidden',
                    maxWidth: { xs: '100vw', sm: width || '1500px' },
                    margin: {
                        sm: 'auto',
                        xs: 0,
                    },
                    height: {
                        sm: 'auto',
                        xs: '100%',
                    },
                    ...sx,
                },
                ...props.PaperProps,
            }}
            {...props}
        >
            <RootContext.Provider value={context}>
                <FormProvider {...methods}>{children}</FormProvider>
            </RootContext.Provider>
        </Dialog>
    )
}

export function transformData(data = {}) {
    return Object.fromEntries(Object.entries(data).map(formatValue))
}

function formatValue([key, value]: [string, any]): [string, any] {
    const datetimeRegex =
        /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})Z/
    const cpfRegex = /(\d{3})\.(\d{3})\.(\d{3})-(\d{2})/
    const pisRegex = /(\d{3})\.(\d{5})\.(\d{2})\.(\d{1})-(\d{1})/

    if (value instanceof File) {
        return [key, value]
    }

    if (Array.isArray(value)) {
        return [key, value]
    }

    if (dayjs.isDayjs(value)) {
        return [key, value.format('YYYY-MM-DD')]
    }

    if (value instanceof Date) {
        return [key, value.toJSON().split('T')[0]]
    }

    if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
            const array = value.map(item =>
                Object.fromEntries(Object.entries(item).map(formatValue)),
            )
            return [key, array]
        }

        return [key, Object.fromEntries(Object.entries(value).map(formatValue))]
    }

    if (typeof value === 'string' && datetimeRegex.test(value)) {
        return [key, value.split('T')[0]]
    }

    if (typeof value === 'string' && cpfRegex.test(value)) {
        return [key, value.replace(/\D/g, '')]
    }

    if (typeof value === 'string' && pisRegex.test(value)) {
        return [key, value.replace(/\D/g, '')]
    }

    if (typeof value === 'string' && value === 'true') {
        return [key, true]
    }

    if (typeof value === 'string' && value === 'S') {
        return [key, true]
    }

    if (typeof value === 'string' && value === 'false') {
        return [key, false]
    }

    if (typeof value === 'string' && value === 'N') {
        return [key, false]
    }

    return [key, value]
}
